import { ReactElement, useMemo, useState } from 'react';
import type { Data, Layout } from 'plotly.js';
import PlotlyChart from './PlotlyChart';
import { formatNumber } from './breakdownFormat';
import NormalisationSlopePlot from './NormalisationSlopePlot';
import { DEFAULT_ANGLE_REF } from '../../../../constants/normalisation';

interface Props {
  config: any;
  session: any;
  sessionRun?: any;
  selectedTraverse?: number;
  sessionAirspeedRef: number | null;
  traverseYaw: number | null;
  traverseAirspeed: number | null;
  angleRef?: number;
}

interface ScatterPoint {
  yaw: number;
  airspeed: number;
  isCurrent: boolean;
}

const YAW_CHANNEL = 'AirYawAbs_AG3';
const SPEED_CHANNEL = 'AirSpeed_vG3';

function collectTraversePoints(traverse: any): { yaws: number[]; airspeeds: number[] } {
  const yaws: number[] = traverse?.[YAW_CHANNEL] ?? [];
  const airspeeds: number[] = traverse?.[SPEED_CHANNEL] ?? [];
  return { yaws, airspeeds };
}

type ViewMode = '3d' | '2d';
type DataLevel = 'traverse' | 'run' | 'config' | 'all';
type NormType = 'standard' | 'flattened';

const YAW_MIN = 0;
const YAW_MAX = 15;
const YAW_STEP = 0.5;
const SPEED_MIN = 5;
const SPEED_MAX = 25;
const SPEED_STEP = 0.5;

function range(min: number, max: number, step: number): number[] {
  const out: number[] = [];
  for (let v = min; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(3)));
  return out;
}

function evalNormalisation(
  yawAbs: number,
  airspeed: number,
  angleRef: number,
  speedRef: number,
  xwSlope: number,
  speedSlope: number,
): number {
  return (yawAbs - angleRef) ** 2 * xwSlope + (airspeed - speedRef) * speedSlope;
}

export default function NormalisationContour({
  config,
  session,
  sessionRun,
  selectedTraverse,
  sessionAirspeedRef,
  traverseYaw,
  traverseAirspeed,
  angleRef = DEFAULT_ANGLE_REF,
}: Props): ReactElement {
  const [viewMode, setViewMode] = useState<ViewMode>('3d');
  const [dataLevel, setDataLevel] = useState<DataLevel>('traverse');
  const [normType, setNormType] = useState<NormType>('standard');
  const xwSlope: number = config?.xw_para_slope ?? 0;
  const speedSlope: number = config?.airspeed_slope ?? 0;
  const speedRef: number = sessionAirspeedRef ?? config?.airspeed_ref ?? 13.4;

  // Compute scatter points based on data level. Each point gets (yaw, airspeed) in the
  // same units as the surface axes; we evaluate z on the surface for placement.
  const scatterPoints = useMemo<ScatterPoint[]>(() => {
    const points: ScatterPoint[] = [];
    const sessionRunId = sessionRun?.id ?? sessionRun?._id ?? sessionRun?.run_id;
    const currentConfig = (session?.configs ?? []).find((c: any) =>
      (c?.runs ?? []).some(
        (r: any) => (r?.id ?? r?._id ?? r?.run_id) === sessionRunId,
      ),
    );

    if (dataLevel === 'traverse') {
      const { yaws, airspeeds } = collectTraversePoints(sessionRun?.traverse);
      const idx = selectedTraverse ?? 0;
      if (Number.isFinite(yaws[idx]) && Number.isFinite(airspeeds[idx])) {
        points.push({ yaw: yaws[idx], airspeed: airspeeds[idx], isCurrent: true });
      }
    } else if (dataLevel === 'run') {
      const { yaws, airspeeds } = collectTraversePoints(sessionRun?.traverse);
      const len = Math.min(yaws.length, airspeeds.length);
      for (let i = 0; i < len; i++) {
        if (Number.isFinite(yaws[i]) && Number.isFinite(airspeeds[i])) {
          points.push({ yaw: yaws[i], airspeed: airspeeds[i], isCurrent: i === selectedTraverse });
        }
      }
    } else if (dataLevel === 'config') {
      (currentConfig?.runs ?? []).forEach((r: any) => {
        const isSelectedRun = (r?.id ?? r?._id ?? r?.run_id) === sessionRunId;
        const { yaws, airspeeds } = collectTraversePoints(r?.traverse);
        const len = Math.min(yaws.length, airspeeds.length);
        for (let i = 0; i < len; i++) {
          if (Number.isFinite(yaws[i]) && Number.isFinite(airspeeds[i])) {
            points.push({
              yaw: yaws[i],
              airspeed: airspeeds[i],
              isCurrent: isSelectedRun && i === selectedTraverse,
            });
          }
        }
      });
    } else {
      (session?.configs ?? []).forEach((c: any) => {
        (c?.runs ?? []).forEach((r: any) => {
          const isSelectedRun = (r?.id ?? r?._id ?? r?.run_id) === sessionRunId;
          const { yaws, airspeeds } = collectTraversePoints(r?.traverse);
          const len = Math.min(yaws.length, airspeeds.length);
          for (let i = 0; i < len; i++) {
            if (Number.isFinite(yaws[i]) && Number.isFinite(airspeeds[i])) {
              points.push({
                yaw: yaws[i],
                airspeed: airspeeds[i],
                isCurrent: isSelectedRun && i === selectedTraverse,
              });
            }
          }
        });
      });
    }
    return points;
  }, [dataLevel, session, sessionRun, selectedTraverse]);

  const surface = useMemo(() => {
    const yaws = range(YAW_MIN, YAW_MAX, YAW_STEP);
    const speeds = range(SPEED_MIN, SPEED_MAX, SPEED_STEP);
    const z = speeds.map((s) =>
      yaws.map((y) => evalNormalisation(y, s, angleRef, speedRef, xwSlope, speedSlope)),
    );
    let zMin = Infinity;
    let zMax = -Infinity;
    z.forEach((row) => row.forEach((v) => {
      if (Number.isFinite(v)) {
        if (v < zMin) zMin = v;
        if (v > zMax) zMax = v;
      }
    }));
    if (!Number.isFinite(zMin)) zMin = 0;
    if (!Number.isFinite(zMax)) zMax = 0;
    return { x: yaws, y: speeds, z, zMin, zMax };
  }, [angleRef, speedRef, xwSlope, speedSlope]);

  const traverseZ =
    traverseYaw != null && traverseAirspeed != null
      ? evalNormalisation(traverseYaw, traverseAirspeed, angleRef, speedRef, xwSlope, speedSlope)
      : null;

  const labelZOffset = Math.max(Math.abs(surface.zMax - surface.zMin) * 0.18, 0.02);

  const yawStep = (YAW_MAX - YAW_MIN) / 4; // 4 contour bands → 5 lines
  const spdStep = (SPEED_MAX - SPEED_MIN) / 4;

  const data = useMemo<Data[]>(() => {
    const traces: Data[] = [
      {
        type: 'surface',
        x: surface.x,
        y: surface.y,
        z: surface.z,
        colorscale: 'Viridis',
        opacity: 0.85,
        showscale: true,
        contours: {
          x: { show: true, color: '#374151', width: 1, start: YAW_MIN, end: YAW_MAX, size: yawStep },
          y: { show: true, color: '#374151', width: 1, start: SPEED_MIN, end: SPEED_MAX, size: spdStep },
        },
        hoverinfo: 'skip',
        name: 'Normalisation',
      } as Data,
    ];

    // Reference marker + label (label raised in Z, includes values)
    traces.push({
      type: 'scatter3d',
      mode: 'markers',
      x: [angleRef],
      y: [speedRef],
      z: [0],
      marker: { size: 6, color: '#000000', symbol: 'diamond' },
      hoverinfo: 'skip',
      showlegend: false,
    });
    traces.push({
      type: 'scatter3d',
      mode: 'text',
      x: [angleRef],
      y: [speedRef],
      z: [labelZOffset],
      text: [`Reference: ${formatNumber(angleRef, 1)}°, ${formatNumber(speedRef, 2)} m/s`],
      textfont: { color: '#000000', size: 12 },
      textposition: 'middle right',
      hoverinfo: 'skip',
      showlegend: false,
    });

    // Other-traverse markers (gray dots, lifted above surface) for run/config/all data levels
    const others = scatterPoints.filter((p) => !p.isCurrent);
    if (others.length > 0) {
      traces.push({
        type: 'scatter3d',
        mode: 'markers',
        x: others.map((p) => p.yaw),
        y: others.map((p) => p.airspeed),
        z: others.map(
          (p) => evalNormalisation(p.yaw, p.airspeed, angleRef, speedRef, xwSlope, speedSlope) + labelZOffset * 0.3,
        ),
        marker: { size: 4, color: '#9ca3af', opacity: 0.7 },
        hoverinfo: 'skip',
        showlegend: false,
      });
    }

    if (traverseZ != null && traverseYaw != null && traverseAirspeed != null) {
      // Traverse marker + label (label raised in Z, includes values)
      traces.push({
        type: 'scatter3d',
        mode: 'markers',
        x: [traverseYaw],
        y: [traverseAirspeed],
        z: [traverseZ],
        marker: { size: 8, color: '#ef4444' },
        hoverinfo: 'skip',
        showlegend: false,
      });
      traces.push({
        type: 'scatter3d',
        mode: 'text',
        x: [traverseYaw],
        y: [traverseAirspeed],
        z: [traverseZ + labelZOffset],
        text: [`Traverse: ${formatNumber(traverseYaw, 2)}°, ${formatNumber(traverseAirspeed, 2)} m/s`],
        textfont: { color: '#b91c1c', size: 12 },
        textposition: 'middle right',
        hoverinfo: 'skip',
        showlegend: false,
      });

      // Walk a step-path *in space* with the horizontal legs always at the *upper* z, so the
      // path stays above the slope regardless of the sign of the normalisation. When positive,
      // legs sit at z=traverseZ and the vertical drops to the reference (z=0). When negative,
      // legs sit at z=0 and the vertical drops down to the traverse (at z=traverseZ).
      if (traverseZ !== 0) {
        const lineColor = '#1e3a8a';
        const positive = traverseZ > 0;
        const upperZ = positive ? traverseZ : 0;

        // L-shape end-point coordinates: when positive we walk *from* the traverse *to* the
        // column above the reference; when negative we walk *from* the reference *to* the
        // column above the traverse.
        const startXY: [number, number] = positive
          ? [traverseYaw, traverseAirspeed]
          : [angleRef, speedRef];
        const cornerXY: [number, number] = positive
          ? [traverseYaw, speedRef]
          : [traverseYaw, speedRef];
        const endXY: [number, number] = positive
          ? [angleRef, speedRef]
          : [traverseYaw, traverseAirspeed];
        const lowerXYZ: [number, number, number] = positive
          ? [angleRef, speedRef, 0]
          : [traverseYaw, traverseAirspeed, traverseZ];

        // Leg 1: first horizontal leg at z=upperZ (parallel to one axis).
        traces.push({
          type: 'scatter3d',
          mode: 'lines',
          x: [startXY[0], cornerXY[0]],
          y: [startXY[1], cornerXY[1]],
          z: [upperZ, upperZ],
          line: { color: lineColor, width: 4 },
          hoverinfo: 'skip',
          showlegend: false,
        });

        // Leg 2: second horizontal leg at z=upperZ (parallel to the other axis).
        traces.push({
          type: 'scatter3d',
          mode: 'lines',
          x: [cornerXY[0], endXY[0]],
          y: [cornerXY[1], endXY[1]],
          z: [upperZ, upperZ],
          line: { color: lineColor, width: 4 },
          hoverinfo: 'skip',
          showlegend: false,
        });

        // Leg 3: vertical difference — drop (or rise) from the theoretical point at upperZ
        // to the lower point on the surface. Length equals |traverseZ|.
        traces.push({
          type: 'scatter3d',
          mode: 'lines',
          x: [endXY[0], lowerXYZ[0]],
          y: [endXY[1], lowerXYZ[1]],
          z: [upperZ, lowerXYZ[2]],
          line: { color: lineColor, width: 4, dash: 'dot' },
          hoverinfo: 'skip',
          showlegend: false,
        });

        // Diamond markers at the two in-space corners (where the legs join).
        traces.push({
          type: 'scatter3d',
          mode: 'markers',
          x: [cornerXY[0], endXY[0]],
          y: [cornerXY[1], endXY[1]],
          z: [upperZ, upperZ],
          marker: { size: 5, color: '#000000', symbol: 'diamond' },
          hoverinfo: 'skip',
          showlegend: false,
        });
      }
    }
    return traces;
  }, [
    surface,
    angleRef,
    speedRef,
    xwSlope,
    speedSlope,
    traverseYaw,
    traverseAirspeed,
    traverseZ,
    labelZOffset,
    yawStep,
    spdStep,
    scatterPoints,
  ]);

  const layout = useMemo<Partial<Layout>>(() => ({
    scene: {
      xaxis: { title: { text: 'Yaw |°|' }, nticks: 5, showspikes: false },
      yaxis: { title: { text: 'Airspeed (m/s)' }, nticks: 5, showspikes: false },
      zaxis: { title: { text: 'Normalisation amount' }, nticks: 5, showspikes: false },
    },
    margin: { l: 0, r: 0, b: 0, t: 10 },
    showlegend: false,
    hovermode: false,
    autosize: true,
  }), []);

  const slopesAvailable = xwSlope !== 0 || speedSlope !== 0;

  const plotData = session?.plot_data;
  const configs = session?.configs;

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-mb-6">
      <div className="cr-flex cr-items-center cr-justify-between cr-mb-3 cr-flex-wrap cr-gap-3">
        <h3 className="font-montserrat cr-text-lg cr-font-semibold cr-text-blue-900">
          Normalisation Contour
        </h3>
        <div className="cr-flex cr-items-center cr-gap-4 cr-text-sm">
          <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="normalisation-view-mode"
              value="3d"
              checked={viewMode === '3d'}
              onChange={() => setViewMode('3d')}
              className="cr-w-4 cr-h-4"
            />
            <span className="cr-font-medium">3-D Contour</span>
          </label>
          <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="normalisation-view-mode"
              value="2d"
              checked={viewMode === '2d'}
              onChange={() => setViewMode('2d')}
              className="cr-w-4 cr-h-4"
            />
            <span className="cr-font-medium">2-D Slopes</span>
          </label>
        </div>
      </div>
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        How raw CdA gets adjusted at any (|yaw|, airspeed). The black diamond is the reference
        operating point (where no normalisation is needed); the red marker is where this traverse
        actually was.
      </p>
      {!slopesAvailable && (
        <div className="cr-text-amber-700 cr-bg-amber-50 cr-rounded cr-p-3 cr-mb-4 cr-text-sm">
          This config has no crosswind or airspeed slope set, so the surface is flat and no
          normalisation is applied.
        </div>
      )}
      <div className="cr-flex cr-items-center cr-gap-4 cr-flex-wrap cr-mb-3 cr-text-sm cr-bg-gray-50 cr-rounded cr-px-3 cr-py-2">
        <span className="cr-font-medium cr-text-gray-700">Show:</span>
        {(['traverse', 'run', 'config', 'all'] as DataLevel[]).map((lvl) => (
          <label key={lvl} className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="normalisation-data-level"
              value={lvl}
              checked={dataLevel === lvl}
              onChange={() => setDataLevel(lvl)}
              className="cr-w-4 cr-h-4"
            />
            <span>{lvl === 'all' ? 'all data' : lvl}</span>
          </label>
        ))}
        {dataLevel === 'all' && (
          <div className="cr-flex cr-items-center cr-gap-3 cr-pl-4 cr-border-l cr-border-gray-300">
            <span className="cr-text-gray-500 cr-italic">normalisation:</span>
            {(['standard', 'flattened'] as NormType[]).map((nt) => (
              <label key={nt} className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
                <input
                  type="radio"
                  name="normalisation-norm-type"
                  value={nt}
                  checked={normType === nt}
                  onChange={() => setNormType(nt)}
                  className="cr-w-4 cr-h-4"
                />
                <span>{nt}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {viewMode === '3d' ? (
        <PlotlyChart data={data} layout={layout} height={520} />
      ) : (
        <Slopes2D
          plotData={plotData}
          configs={configs}
          session={session}
          sessionRun={sessionRun}
          selectedTraverse={selectedTraverse ?? 0}
          normType={dataLevel === 'all' ? normType : 'standard'}
          dataLevel={dataLevel}
          angleRef={angleRef}
          speedRef={speedRef}
        />
      )}
      <ParameterEquations
        traverseYaw={traverseYaw}
        traverseAirspeed={traverseAirspeed}
        angleRef={angleRef}
        speedRef={speedRef}
        xwSlope={xwSlope}
        speedSlope={speedSlope}
      />
    </div>
  );
}

// Helper: average of finite numbers in an array, or 0 if no finite values.
function meanFinite(arr: number[] | undefined): number {
  if (!arr || arr.length === 0) return 0;
  let sum = 0;
  let n = 0;
  for (const v of arr) {
    if (Number.isFinite(v)) {
      sum += v;
      n++;
    }
  }
  return n > 0 ? sum / n : 0;
}

// Per-plot Y-channel used to compute the flattened-mode config offset (matches
// the channel each plot uses on its Y axis).
const FLATTEN_Y_CHANNEL: Record<'yaw_plot' | 'speed_plot', string> = {
  yaw_plot: 'CdAXWnNAS_xG3',
  speed_plot: 'CdAXWnNX_xG3',
};

function Slopes2D({
  plotData,
  configs,
  session,
  sessionRun,
  selectedTraverse,
  normType,
  dataLevel,
  angleRef,
  speedRef,
}: {
  plotData: any;
  configs: any[] | undefined;
  session: any;
  sessionRun: any;
  selectedTraverse: number;
  normType: NormType;
  dataLevel: DataLevel;
  angleRef: number;
  speedRef: number;
}): ReactElement {
  // Resolve which config index in session.configs contains the selected run, so the
  // 'config' scope in NormalisationSlopePlot can pull the right per-config tuple.
  const configIdx = useMemo(() => {
    const sessionRunId = sessionRun?.id ?? sessionRun?._id ?? sessionRun?.run_id;
    if (sessionRunId == null) return -1;
    const list = session?.configs ?? [];
    return list.findIndex((c: any) =>
      (c?.runs ?? []).some((r: any) => (r?.id ?? r?._id ?? r?.run_id) === sessionRunId),
    );
  }, [session, sessionRun]);

  // Flattened-mode offset for the selected run's config, per plot type. Defined as
  // mean(config[i].Y_CHANNEL across all its traverses) − mean(configA.Y_CHANNEL across
  // all its traverses). For configIdx == 0 (Config A) this is 0 by construction, so
  // Config A's traverse marker doesn't move; non-A configs shift by their offset.
  const flattenedOffsets = useMemo(() => {
    const list: any[] = session?.configs ?? [];
    const computeFor = (plotType: 'yaw_plot' | 'speed_plot') => {
      const channel = FLATTEN_Y_CHANNEL[plotType];
      const configMean = (cfg: any): number => {
        const all: number[] = [];
        (cfg?.runs ?? []).forEach((r: any) => {
          const arr: number[] = r?.traverse?.[channel] ?? [];
          arr.forEach((v) => {
            if (Number.isFinite(v)) all.push(v);
          });
        });
        return meanFinite(all);
      };
      const meanA = list[0] ? configMean(list[0]) : 0;
      return list.map((cfg: any) => configMean(cfg) - meanA);
    };
    return {
      yaw_plot: computeFor('yaw_plot'),
      speed_plot: computeFor('speed_plot'),
    };
  }, [session]);

  if (!plotData || !configs) {
    return (
      <div className="cr-p-6 cr-text-gray-500 cr-italic">
        2-D slope plots are not available for this session.
      </div>
    );
  }
  const yawOffset = flattenedOffsets.yaw_plot[configIdx] ?? 0;
  const speedOffset = flattenedOffsets.speed_plot[configIdx] ?? 0;
  return (
    <div className="cr-grid cr-grid-cols-1 lg:cr-grid-cols-2 cr-gap-4">
      <NormalisationSlopePlot
        plotType="yaw_plot"
        dataLevel={dataLevel}
        session={session}
        plotData={plotData?.yaw_plot?.[normType] ?? plotData?.yaw_plot?.standard ?? null}
        configIdx={configIdx}
        currentRun={sessionRun}
        selectedTraverse={selectedTraverse}
        configs={configs}
        xRef={angleRef}
        normType={normType}
        flattenedOffset={yawOffset}
      />
      <NormalisationSlopePlot
        plotType="speed_plot"
        dataLevel={dataLevel}
        session={session}
        plotData={plotData?.speed_plot?.[normType] ?? plotData?.speed_plot?.standard ?? null}
        configIdx={configIdx}
        currentRun={sessionRun}
        selectedTraverse={selectedTraverse}
        configs={configs}
        xRef={speedRef}
        normType={normType}
        flattenedOffset={speedOffset}
      />
    </div>
  );
}

interface ParamEqProps {
  traverseYaw: number | null;
  traverseAirspeed: number | null;
  angleRef: number;
  speedRef: number;
  xwSlope: number;
  speedSlope: number;
}

function ParameterEquations({
  traverseYaw,
  traverseAirspeed,
  angleRef,
  speedRef,
  xwSlope,
  speedSlope,
}: ParamEqProps): ReactElement {
  const yawDiff =
    traverseYaw != null ? (traverseYaw - angleRef) ** 2 * xwSlope : null;
  const speedDiff =
    traverseAirspeed != null ? (traverseAirspeed - speedRef) * speedSlope : null;
  const total =
    yawDiff != null && speedDiff != null ? yawDiff + speedDiff : null;

  return (
    <div className="cr-mt-5 cr-pt-4 cr-border-t cr-border-gray-200 cr-text-sm">
      <div className="cr-grid cr-grid-cols-1 md:cr-grid-cols-2 cr-gap-6 cr-mb-4">
        <EquationRow
          label="Yaw difference"
          equation="(|yaw| − angle_ref)² × xw_para_slope"
          substitution={
            traverseYaw != null
              ? `(${formatNumber(traverseYaw, 2)} − ${formatNumber(angleRef, 1)})² × ${formatNumber(xwSlope, 6)}`
              : null
          }
          result={yawDiff}
        />
        <EquationRow
          label="Airspeed difference"
          equation="(airspeed − airspeed_ref) × airspeed_slope"
          substitution={
            traverseAirspeed != null
              ? `(${formatNumber(traverseAirspeed, 2)} − ${formatNumber(speedRef, 2)}) × ${formatNumber(speedSlope, 6)}`
              : null
          }
          result={speedDiff}
        />
      </div>
      <div className="cr-pt-3 cr-border-t cr-border-gray-200 cr-flex cr-flex-wrap cr-items-baseline cr-gap-x-2">
        <span className="cr-font-medium cr-text-blue-900">Total normalisation</span>
        <span className="cr-font-mono cr-text-gray-700">= yaw_diff + airspeed_diff</span>
        {yawDiff != null && speedDiff != null && total != null && (
          <span className="cr-font-mono cr-text-gray-700">
            = {formatNumber(yawDiff, 6)} + {formatNumber(speedDiff, 6)}
            <span className="cr-text-blue-900 cr-font-semibold"> = {formatNumber(total, 6)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function EquationRow({
  label,
  equation,
  substitution,
  result,
}: {
  label: string;
  equation: string;
  substitution: string | null;
  result: number | null;
}): ReactElement {
  return (
    <div>
      <div className="cr-font-medium cr-text-gray-800">{label}</div>
      <div className="cr-font-mono cr-text-gray-700 cr-mt-1">{equation}</div>
      {substitution && (
        <div className="cr-font-mono cr-text-gray-600 cr-mt-1">= {substitution}</div>
      )}
      {result != null && (
        <div className="cr-font-mono cr-mt-1 cr-text-gray-900 cr-font-medium">
          = {formatNumber(result, 6)}
        </div>
      )}
    </div>
  );
}
